import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Col, Container, FormGroup, Label, Row } from "reactstrap";
import EmployeeCallType from "../../Components/Common/EmployeeCallType";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useLocation, useNavigate } from "react-router-dom";
import EmployeeSelect from "../../Components/Common/EmployeeSelect";
import TeamSelect from "../../Components/Common/TeamSelect";
import { useProfileData } from "../../Components/Hooks/UserHooks";
import { getPreviousCallAnalysisDate, setCurrentDate } from "../../Components/Common/DefaultDateSet";
import { formatDate, formatDateTime } from "../../Components/Common/FormDateTime";
import { DatePicker } from "../../Components/Common/Flatpicker";
import CalculateRemainingDays from "../../Components/Common/CalculateRemainingDays";
import SubExpired from "../../Components/Common/subExpired";
import { POST_EMPLOYEE_CALLS_ANALYSIS_GRAPH_API } from "../../helpers/url_helper";
import { APIClient } from "../../helpers/api_helper";
import { HandleErrorMessage } from "../../Components/Common/HandleErrorMessage";
import { CommanDataTables } from "../../Components/Common/DashboardDataTbale";
import { formatTime } from "../../Components/Common/FlatePicker";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import { formatFlatMonthPicker } from "../../Components/Common/ToCamelCase";

const api = new APIClient();

const DetailedAnalysis = () => {
   // default date
      const previewsDate = getPreviousCallAnalysisDate();
      const currentDate = setCurrentDate();
      
  const navigate = useNavigate();
  const location = useLocation();
  const { employee } = location.state || {};
  const permissionType = JSON.parse(localStorage.getItem("permission"));
  const viewReportPermission = permissionType?.includes("VIEW_REPORT");
  const loginType = JSON.parse(localStorage.getItem("logintype"));
  const { profileData } = useProfileData();
  const BooleanWithSingleEmployeeLogin = loginType === "employee" && permissionType.length === 0;

  const params = new URLSearchParams(window.location.search);
  const date = params.get("date"); // Retrieve the 'date' parameter

  // State for data
  const [callSummaryData, setCallSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState("");
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(true);


   // date picker state
      const [selectedFromDate, setSelectedFromDate] = useState(previewsDate);
      const [selectedToDate, setSelectedToDate] = useState(currentDate);


  const [selectedOptions, setSelectedOptions] = useState([]);
  const [teamIndu, setTeamIndu] = useState([]);
  const [selectedEmployeeOptions, setSelectedEmployeeOptions] = useState(employee || []);
  const [EmployeeData, setEmployeeData] = useState([]);
  const [EmployeeDefaultWithoutTeam, setEmployeeDefaultWithoutTeam] = useState([]);
  const [allEmployeesLoaded, setAllEmployeesLoaded] = useState(false);

  // Flatpicker DateTime
  const formattedStartTime = formatDateTime(selectedFromDate);
  let formattedEndTime = formatDateTime(selectedToDate);


  // Memoized formatted dates
  const formattedDates = useMemo(
    () => formatFlatMonthPicker(selectedFromDate, selectedToDate),
    [selectedFromDate, selectedToDate]
  );


  // Applied filters state
  const [appliedFilters, setAppliedFilters] = useState(null);

  // Pagination handlers
  const handlePerRowsChange = (newPerPage) => setPerPage(newPerPage);
  const handlePageChange = (page) => setCurrentPage(page);

  const remainingDays = CalculateRemainingDays();
  // Handle date changes
  const handleFromDateChange = (dates) => {
    const newFromDate = dates[0];
    setSelectedFromDate(newFromDate);
  };

  const handleToDateChange = (dates) => {
    const newToDate = new Date(dates);
    setSelectedToDate(newToDate);
  };

  // Reset filters
  const resetInputHistory = () => {
    setSelectedToDate(currentDate + "T23:59:59");
    setSelectedFromDate(previewsDate + "T00:00:00");
    setSelectedOptions([]);
    // Don't reset employee selection on reset to maintain "all employees selected" behavior
    setError("");

    // After resetting, trigger API call with all employees
    if (EmployeeDefaultWithoutTeam.length > 0 && !BooleanWithSingleEmployeeLogin) {
      setSelectedEmployeeOptions(EmployeeDefaultWithoutTeam);
      const allEmployeeIds = EmployeeDefaultWithoutTeam.map(emp => emp.id);
      fetchEmployeeCallSummary(1, 10, allEmployeeIds);
    } else if (BooleanWithSingleEmployeeLogin && profileData?._id) {
      fetchEmployeeCallSummary(1, 10, [profileData._id]);
    }
  };

  // Function to load all employees
  const loadAllEmployees = async () => {
    try {
      if (EmployeeDefaultWithoutTeam.length > 0 && !allEmployeesLoaded) {
        setSelectedEmployeeOptions(EmployeeDefaultWithoutTeam);
        setAllEmployeesLoaded(true);

        // If this is not a single employee login, fetch data for all employees
        if (!BooleanWithSingleEmployeeLogin) {
          const allEmployeeIds = EmployeeDefaultWithoutTeam.map(emp => emp.id);
          fetchEmployeeCallSummary(1, 10, allEmployeeIds);
        }
      }
    } catch (error) {
      console.error("Error loading all employees:", error);
    }
  };

  // Fetch employee call summary data
  const fetchEmployeeCallSummary = async (page = currentPage, size = perPage, employeeIds = [], searchTerm = "") => {
    setIsLoading(true);
    try {
      let ids = employeeIds.length > 0
        ? employeeIds
        : (appliedFilters?.employeeIds ? appliedFilters.employeeIds : []);

      if (ids.length === 0 && EmployeeDefaultWithoutTeam.length > 0 && !BooleanWithSingleEmployeeLogin) {
        ids = EmployeeDefaultWithoutTeam.map(emp => emp.id);
      } else if (ids.length === 0 && BooleanWithSingleEmployeeLogin && profileData?._id) {
        ids = [profileData._id];
      }

      // Prepare request data
      let data = {
        startDate: selectedFromDate,
        endDate: selectedToDate,
        employeeIds: ids,
        callType: selectedOptions?.map((option) => option?.value) || [],
      };

      // Make API request
      const response = await api.create(
        `${POST_EMPLOYEE_CALLS_ANALYSIS_GRAPH_API}`,
        data
      );

      if (response) {
        const apiResponse = response.data;



        // Check if the response is an array or has a paginatedResults property
        const resultsArray = Array.isArray(apiResponse)
          ? apiResponse
          : (apiResponse?.paginatedResults || []);

        // Transform the API response data to match the table structure
        let formattedData = resultsArray.map((item) => ({
          detailedusername: item.employee_name || "Unknown",
          detailedmonth: item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : "-",
          datailedtotalcall: item.total_calls || 0,
          datailedmomchange: `${(item.mom_change?.total || 0).toFixed(2)}%`,
          detailedincomingcall: item.incoming_call || 0,
          detailedmommom: `${(item.mom_change?.incoming || 0).toFixed(2)}%`,
          detailedoutgoingmain: item.outgoing_call || 0,
          detailedmommomtoo: `${(item.mom_change?.outgoing || 0).toFixed(2)}%`,
          detailedmainmissedcall: item.missed_call || 0,
          detailedmainthree: `${(item.mom_change?.missed || 0).toFixed(2)}%`,
          detailedrejectedcallmain: item.rejected_call || 0,
          detailedmainfourcall: `${(item.mom_change?.rejected || 0).toFixed(2)}%`,
          detailedavgcallduration: item.average_call_duration
            ? formatTime(item.average_call_duration).slice(0, 8) + "s"
            : "-",
          detailedmomcalldeatiled: `${(item.mom_change?.average_duration || 0).toFixed(2)}%`,
          profileImageUrl: item.profile_image_url || null,
          employeeId: item.employee_id || "-",
        }));

        console.log("Formatted Data:", formattedData);

        setCallSummaryData(formattedData);

        // Handle total count based on response format
        const totalCount = apiResponse.totalCount || resultsArray.length || 0;
        setTotalRows(totalCount);
      }
    } catch (error) {
      console.error("API Error:", error);
      HandleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle apply button click
  const handleSubmit = () => {
    if (BooleanWithSingleEmployeeLogin && profileData?._id) {
      setError("");

      const newFilters = {
        startDate: formattedDates.formattedStartDate,
        endDate: formattedDates.formattedEndDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        employeeIds: [profileData._id],
        callType: selectedOptions?.map((option) => option?.value) || [],
      };

      setAppliedFilters(newFilters);
      setCurrentPage(1);
      setPerPage(10);

      fetchEmployeeCallSummary(1, 10, [profileData._id]);
      return;
    }

    let employeeIds = selectedEmployeeOptions?.map((userid) => userid?.id) || [];

    // If no employees selected, use all employees
    if (employeeIds.length === 0 && EmployeeDefaultWithoutTeam.length > 0) {
      employeeIds = EmployeeDefaultWithoutTeam.map(emp => emp.id);
      setSelectedEmployeeOptions(EmployeeDefaultWithoutTeam);
    }

    setError("");

    const newFilters = {
      startDate: formatFlatMonthPicker(selectedFromDate),
      endDate: formatFlatMonthPicker(selectedToDate),
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      employeeIds: employeeIds,
      callType: selectedOptions?.map((option) => option?.value) || [],
    };

    // Set applied filters and reset pagination
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    setPerPage(10);


    // Fetch data with new filters
    fetchEmployeeCallSummary(1, 10, employeeIds);
  };

  // Load all employees when component mounts or employee data becomes available
  useEffect(() => {
    if (EmployeeDefaultWithoutTeam.length > 0 && !allEmployeesLoaded) {
      loadAllEmployees();
    }
  }, [EmployeeDefaultWithoutTeam]);

  // Initial data fetch on component mount
  useEffect(() => {
    if (employee) {
      const employeeIds = employee.map(emp => emp.id);
      fetchEmployeeCallSummary(1, 10, employeeIds);
    } else if (BooleanWithSingleEmployeeLogin && profileData && profileData._id) {
      fetchEmployeeCallSummary(1, 10, [profileData._id]);
    } else {
      if (EmployeeDefaultWithoutTeam.length > 0) {
        const allEmployeeIds = EmployeeDefaultWithoutTeam.map(emp => emp.id);
        setSelectedEmployeeOptions(EmployeeDefaultWithoutTeam);
        fetchEmployeeCallSummary(1, 10, allEmployeeIds);
      }
    }
  }, [profileData]);

  // Refetch when page or perPage changes
  useEffect(() => {
    if (appliedFilters) {
      fetchEmployeeCallSummary();
    }
  }, [currentPage, perPage]);

  // Table columns definition
  const DetailedAnalysiscallTable = [
    {
      name: <span className="font-weight-bold fs-13">S No.</span>,
      selector: (row, index) => index + 1,
      sortable: false,
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Employee Name</span>,
      selector: (row) => row.detailedusername,
      cell: (row) => {
        const getBackgroundColor = (name) => {
          if (!name) return "DodgerBlue";
          const firstChar = name.charAt(0).toUpperCase();
          const colorMap = {
            A: "Tomato", B: "Orange", C: "Gold", D: "Green", E: "Teal",
            F: "Blue", G: "Purple", H: "Indigo", I: "Crimson", J: "Brown",
            K: "SlateBlue", L: "Olive", M: "SeaGreen", N: "SteelBlue",
            O: "DarkOrange", P: "DarkGreen", Q: "DarkCyan", R: "RoyalBlue",
            S: "HotPink", T: "SandyBrown", U: "DarkViolet", V: "MediumSlateBlue",
            W: "CornflowerBlue", X: "DarkKhaki", Y: "Chocolate", Z: "FireBrick",
          };
          return colorMap[firstChar] || "DodgerBlue";
        };

        const bgColor = getBackgroundColor(row.detailedusername);

        return (
          <div className="d-flex align-items-center">
            {row.profileImageUrl ? (
              <img
                src={row.profileImageUrl}
                alt="Profile"
                style={{
                  width: "35px",
                  height: "35px",
                  objectFit: "cover",
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
              />
            ) : (
              <div
                className="rounded-circle d-flex justify-content-center align-items-center"
                style={{
                  width: "35px",
                  height: "35px",
                  background: bgColor,
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "#fff",
                }}
              >
                {row.detailedusername ? row.detailedusername.charAt(0).toUpperCase() : "?"}
              </div>
            )}
            <span>{row.detailedusername || "Unknown"}</span>
          </div>
        );
      },
      sortable: true,
      width: "200px",
    },
    {
      name: <span className="font-weight-bold fs-13">Total Calls </span>,
      selector: (row) => row.datailedtotalcall || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">MoM % Change</span>,
      selector: (row) => row.datailedmomchange || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Incoming</span>,
      selector: (row) => row.detailedincomingcall || "-",
      sortable: true,
      width: "200px",
    },
    {
      name: <span className="font-weight-bold fs-13">MoM %</span>,
      selector: (row) => row.detailedmommom || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Outgoing </span>,
      selector: (row) => row.detailedoutgoingmain || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">MoM %</span>,
      selector: (row) => row.detailedmommomtoo || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Missed Calls </span>,
      selector: (row) => row.detailedmainmissedcall || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">MoM %</span>,
      selector: (row) => row.detailedmainthree || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Rejected Calls</span>,
      selector: (row) => row.detailedrejectedcallmain || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">MoM %</span>,
      selector: (row) => row.detailedmainfourcall || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Avg. Call Duration</span>,
      selector: (row) => row.detailedavgcallduration || "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">MoM %</span>,
      selector: (row) => row.detailedmomcalldeatiled || "-",
      sortable: true,
    },
  ];

  const handleClose = () => {
    setIsExpiredModalOpen(false);
    navigate("/planPackage");
  };

  return (
    <React.Fragment>
      {remainingDays === 0 && isExpiredModalOpen && (
        <SubExpired
          onClose={handleClose}
          profileData={profileData?.selectedPlanDetails?.planName}
          remainingDays={remainingDays}
        />
      )}
      <div className="page-content sn-call-report-page-top">
        <Container fluid>
          <BreadCrumb title="Detailed Analysis" pageTitle="Detailed Analysis" />

          <Card className="h-100">
            <CardBody>
              <Row className="sn-header-select-employess-home sn-team-managment-tble sn-top-inputs-heading">
                {/* From Date */}
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 col-xs-12">
                  <div className="sn-call-summary-date-picker">
                    <DatePicker
                      labelContent="From Date"
                      getDate={selectedFromDate}
                      // options={{
                      //   disable: [
                      //     {
                      //       from: new Date(),
                      //     },
                      //   ],
                      //   dateFormat: "M, Y",
                      //   maxDate: new Date(new Date().setDate(new Date().getDate())),
                      // }}
                      options={{
                        plugins: [
                          new monthSelectPlugin({
                            shorthand: true,
                            dateFormat: "F, Y",
                            theme: "light",
                          }),
                        ],
                        maxDate: new Date(),
                      }}
                      onChange={handleFromDateChange}
                    />
                  </div>
                </div>

                {/* To Date */}
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 col-xs-12">
                  <div className="sn-call-summary-date-picker">
                    <DatePicker
                      labelContent="To Date"
                      getDate={selectedToDate}
                      options={{
                        plugins: [
                          new monthSelectPlugin({
                            shorthand: true,
                            dateFormat: "F, Y",
                            theme: "light",
                          }),
                        ],
                        maxDate: new Date(),
                      }}
                      onChange={handleToDateChange}
                    />
                  </div>
                </div>

                {/* Team Select */}
                {((viewReportPermission && loginType === "employee") || loginType === "organization") && (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 col-xs-12">
                    <TeamSelect
                      setSelectedEmployeeOptions={setSelectedEmployeeOptions}
                      setSelectedOptions={setSelectedOptions}
                      teamIndu={teamIndu}
                      setTeamIndu={setTeamIndu}
                      labelContent="Select Team"
                    />
                  </div>
                )}

                {/* Employee Select */}
                {((viewReportPermission && loginType === "employee") || loginType === "organization") && (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 col-xs-12">
                    <EmployeeSelect
                      setEmployeeDefaultWithoutTeam={setEmployeeDefaultWithoutTeam}
                      EmployeeDefaultWithoutTeam={EmployeeDefaultWithoutTeam}
                      error={error}
                      setError={setError}
                      EmployeeDefault={teamIndu.length === 0 ? EmployeeDefaultWithoutTeam : EmployeeData}
                      setEmployeeDefault={setEmployeeData}
                      teamIndu={teamIndu}
                      selectedEmployeeOptions={selectedEmployeeOptions}
                      setSelectedEmployeeOptions={
                        setSelectedEmployeeOptions
                      }
                      labelContent="Select Employee"
                    />
                  </div>
                )}

                {/* Call Type */}
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 sn-header-select-employess-home">
                  <FormGroup>
                    <EmployeeCallType
                      labelContent="Select Call Type"
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                    />
                  </FormGroup>
                </div>

                {/* Apply/Reset Buttons */}
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 col-xs-12">
                  <FormGroup>
                    <Label for="exampleSelect"></Label>
                    <div id="exampleSelect" className="sn-call-apply-btn-reset mt-2">
                      <Button
                        id="exampleSelect"
                        type="submit"
                        color="danger"
                        className=""
                        onClick={handleSubmit}
                        disabled={isLoading}
                      >
                        Apply
                      </Button>
                      <Button
                        className=""
                        style={{ marginLeft: "10px" }}
                        id="exampleSelect"
                        outline
                        color="danger"
                        onClick={resetInputHistory}
                        disabled={isLoading}
                      >
                        Reset
                      </Button>
                    </div>
                  </FormGroup>
                </div>
              </Row>

              {/* Table */}
              <Row>
                <div className="">
                  <div className="flex-grow-1 ms-2">
                    <div className="sn-lead-report-manavgment-tble-ui">
                      <CommanDataTables
                        column={DetailedAnalysiscallTable}
                        handlePerRowsChange={handlePerRowsChange}
                        isLoading={isLoading}
                        handlePageChange={handlePageChange}
                        showSearch={false}
                        tableData={callSummaryData}
                        totalRows={totalRows}
                        currentPage={currentPage}
                        perPage={perPage}
                      />
                    </div>
                  </div>
                </div>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DetailedAnalysis;